import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Car, Loader2, RefreshCw, Phone, Mail, MapPin, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';

import { API } from '../../config';
const tok = () => localStorage.getItem('tp_admin_token');

const StatusBadge = ({ status }) => {
  const map = {
    confirmed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {(status || 'unknown').toUpperCase()}
    </span>
  );
};

const AdminCabBookings = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = async () => {
    try {
      const r = await axios.get(`${API}/admin/cabs/bookings`, { headers: { Authorization: `Bearer ${tok()}` } });
      setItems(r.data || []);
    } catch (e) {
      toast({ title: 'Failed to load cab bookings' });
    }
  };

  const load = async () => {
    setLoading(true);
    await fetchBookings();
    setLoading(false);
  };

  useEffect(() => { fetchBookings().finally(() => setLoading(false)); }, []);

  const confirmedCount = items.filter((i) => i.status === 'confirmed').length;

  return (
    <div data-testid="admin-cab-bookings">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-tp-navy flex items-center gap-2"><Car size={22} className="text-tp-orange" /> Cab Bookings</h1>
          <p className="text-sm text-gray-500">{items.length} total · {confirmedCount} confirmed</p>
        </div>
        <Button onClick={load} variant="outline" disabled={loading} data-testid="cab-bookings-refresh">
          {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />} Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-tp-violet" size={32} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-gray-500 bg-white rounded-2xl border border-gray-100">
          <Car size={40} className="mx-auto mb-3 text-gray-300" />
          No cab bookings yet.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-gray-400 border-b border-gray-100">
                <th className="px-4 py-3">Booking Ref</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Pickup</th>
                <th className="px-4 py-3">Cab / Vendor</th>
                <th className="px-4 py-3">Fare</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Booked</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60 align-top" data-testid="cab-booking-row">
                  <td className="px-4 py-3">
                    <div className="font-bold text-tp-ink">{b.booking_reference_number || '—'}</div>
                    {b.trn && <div className="text-[11px] text-gray-400">TRN {b.trn}</div>}
                    <div className="text-[10px] text-gray-300">ext {b.ext_booking_number}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-tp-ink">{b.traveler_name}</div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-1"><Phone size={11} /> {b.traveler_mobile_no}</div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-1"><Mail size={11} /> {b.traveler_email_id}</div>
                  </td>
                  <td className="px-4 py-3 max-w-[220px]">
                    <div className="text-tp-ink flex items-start gap-1"><MapPin size={12} className="mt-0.5 text-tp-coral shrink-0" /> <span className="truncate">{b.pickup_area}</span></div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5"><Calendar size={11} /> {b.pickup_datetime}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-tp-ink">{b.category_name || b.category_id}</div>
                    <div className="text-[11px] text-gray-500">{b.vendor_name || b.vendor_id || '—'}</div>
                  </td>
                  <td className="px-4 py-3 font-bold text-tp-violet-deep">₹{b.fare_amount}</td>
                  <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                  <td className="px-4 py-3 text-[11px] text-gray-500">{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminCabBookings;
